import { env } from '@/lib/env';
import { useSessionStore } from '@/store/session.store';

/**
 * Typed fetch wrapper used by every service in `services/*`.
 *
 * Design:
 * - Pulls the bearer token from `SessionStore` (populated by
 *   `<ApiAuthBridge />` from Auth0's `getAccessTokenSilently`).
 * - Serializes bodies to JSON unless the caller opts into FormData (used
 *   by cover-image upload).
 * - Normalizes non-2xx responses into `ApiError` so call sites can show
 *   user-visible messages without re-parsing JSON.
 * - Treats 204 / empty responses as `undefined` so DELETE + toggle calls
 *   don't choke on empty body parsing.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

type UnauthorizedHandler = () => void;
let unauthorizedHandler: UnauthorizedHandler | null = null;
let unauthorizedFired = false;

/**
 * Register a callback invoked the first time `apiRequest` sees a 401.
 * Installed once from `<ApiAuthBridge />` so it can reach Auth0's
 * `logout()` and the Zustand stores. Subsequent 401s are swallowed
 * (debounced) until `resetUnauthorizedHandler()` is called — preventing
 * a wave of parallel failures from triggering N logouts.
 */
export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  unauthorizedHandler = handler;
}

export function resetUnauthorizedHandler(): void {
  unauthorizedFired = false;
}

type QueryValue = string | number | boolean | Array<string | number> | null | undefined;
export type QueryParams = Record<string, QueryValue>;

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  query?: QueryParams;
  body?: unknown;
  /** When true, `body` is passed through untouched (used for multipart). */
  raw?: boolean;
  headers?: Record<string, string>;
  /** Override bearer token (mostly for tests). */
  token?: string | null;
  signal?: AbortSignal;
}

function buildQueryString(query?: QueryParams): string {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      for (const item of value) params.append(key, String(item));
    } else {
      params.set(key, String(value));
    }
  }
  const qs = params.toString();
  return qs.length ? `?${qs}` : '';
}

export async function apiRequest<TResponse>(
  path: string,
  options: RequestOptions = {},
): Promise<TResponse> {
  const { method = 'GET', query, body, raw = false, headers = {}, token, signal } = options;

  const base = env.apiBaseUrl || '';
  const url = `${base}${path}${buildQueryString(query)}`;

  const accessToken = token ?? useSessionStore.getState().accessToken;
  const finalHeaders: Record<string, string> = {
    Accept: 'application/json',
    ...headers,
  };
  if (accessToken) {
    finalHeaders.Authorization = `Bearer ${accessToken}`;
  }

  let finalBody: BodyInit | undefined;
  if (body !== undefined && body !== null) {
    if (raw) {
      finalBody = body as BodyInit;
    } else {
      finalHeaders['Content-Type'] = 'application/json';
      finalBody = JSON.stringify(body);
    }
  }

  const response = await fetch(url, {
    method,
    headers: finalHeaders,
    body: finalBody,
    signal,
    credentials: 'include',
  });

  if (response.status === 204) {
    return undefined as TResponse;
  }

  const contentType = response.headers.get('content-type') ?? '';
  const isJson = contentType.includes('application/json');
  const payload: unknown = isJson ? await response.json().catch(() => undefined) : undefined;

  if (!response.ok) {
    const message =
      (isJson && payload && typeof payload === 'object' && 'message' in payload
        ? String((payload as { message: unknown }).message)
        : undefined) ??
      response.statusText ??
      'Request failed';
    if (response.status === 401 && unauthorizedHandler && !unauthorizedFired) {
      unauthorizedFired = true;
      unauthorizedHandler();
    }
    throw new ApiError(response.status, message, payload);
  }

  return payload as TResponse;
}
